import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown, Download, Eye, Mail, QrCode, X } from 'lucide-react';
import api from '../api/axios';
import { escapeCsvCell } from '../utils/csv';

function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

// dev_id the backend understands as "every device under this CDP"
const ALL_DEVICES = 'ALL';

// The report can span several devices, so each row carries its own DevID.
function rowDevId(row) {
  return row.DevID || '';
}

function rowDate(row) {
  return row.Date || row.CreatedAt || '';
}

export default function DeviceUsageReport() {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(() => formatDate(new Date()));
  const [reportData, setReportData] = useState([]);
  const [devID, setDevID] = useState('');
  const [devices, setDevices] = useState([]);
  const [loginDevId, setLoginDevId] = useState('');
  const [selectedDevId, setSelectedDevId] = useState('');
  const [viewingAllDevices, setViewingAllDevices] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterPatiName, setFilterPatiName] = useState('');
  const [emailModalRow, setEmailModalRow] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);
  const [qrModalRow, setQrModalRow] = useState(null);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [qrExpiresAt, setQrExpiresAt] = useState(null);
  const [isLoadingQr, setIsLoadingQr] = useState(false);
  const [qrError, setQrError] = useState(null);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Devices of the same CDP as the logged-in device; the report defaults to the
  // logged-in device and can be widened to all of them.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/webapi/devicelist');
        if (cancelled) return;
        const data = response.data || {};
        setDevices(data.devices || []);
        setLoginDevId(data.loginDevId || '');
        setSelectedDevId(prev => prev || data.loginDevId || '');
      } catch (err) {
        console.error('Error fetching device list:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const deviceLabel = (device) => {
    const parts = [device.devId, device.serialNo, device.location].filter(Boolean);
    const label = parts.length > 1 ? `${parts[0]} (${parts.slice(1).join(' - ')})` : parts[0] || '';
    return device.devId === loginDevId ? `${label} - this device` : label;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedData = useMemo(() => {
    let data = reportData;
    if (!sortColumn || data.length === 0) return data;
    return [...data].sort((a, b) => {
      const cmp = String(a[sortColumn] ?? '').localeCompare(String(b[sortColumn] ?? ''));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [reportData, sortColumn, sortDirection]);

  const handleFetchReport = async () => {
    setError(null);
    setIsLoading(true);
    setReportData([]);
    setDevID('');
    setSortColumn(null);
    setSortDirection('asc');

    try {
      const response = await api.get('/webapi/deviceusagereport', {
        params: {
          start_date: startDate,
          end_date: endDate,
          patient_name: filterPatiName,
          dev_id: selectedDevId || loginDevId,
        },
      });
      const data = response.data || {};
      setDevID(data.DevID || '');
      setViewingAllDevices(data.selectedDevId === ALL_DEVICES);
      setReportData(data.records || []);
    } catch (err) {
      setError('Failed to fetch usage report.');
      console.error('Error fetching device usage report:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewPdf = async (devId, reportId) => {
    const newTab = window.open('', '_blank');
    try {
      const response = await api.get('/webapi/DownloadBackupPDF', {
        params: { devId, reportId },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (newTab) newTab.location.href = url;
    } catch (err) {
      if (newTab) newTab.close();
      setError('Failed to open PDF.');
      console.error('Error opening PDF:', err);
    }
  };

  const openEmailModal = (row) => {
    setEmailModalRow(row);
    setEmailInput(row.Email || '');
    setEmailError(null);
    setEmailSuccess(null);
  };

  const closeEmailModal = () => {
    setEmailModalRow(null);
    setEmailInput('');
    setEmailError(null);
    setEmailSuccess(null);
    setIsSendingEmail(false);
  };

  const handleSendEmail = async () => {
    if (!emailModalRow) return;
    setIsSendingEmail(true);
    setEmailError(null);
    setEmailSuccess(null);
    try {
      const body = new URLSearchParams();
      body.append('reportId', emailModalRow.ReportID);
      body.append('toEmail', emailInput);
      body.append('devId', rowDevId(emailModalRow));
      await api.post('/webapi/SendReportToEmail', body);
      setEmailSuccess('Report sent successfully.');
    } catch (err) {
      setEmailError('Failed to send report.');
      console.error('Error sending report by email:', err);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const openQrModal = async (row) => {
    setQrModalRow(row);
    setQrImageUrl(null);
    setQrExpiresAt(null);
    setQrError(null);
    setIsLoadingQr(true);

    try {
      const response = await api.get('/webapi/ReportQr', {
        params: { devId: rowDevId(row), reportId: row.ReportID },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'image/png' });
      setQrImageUrl(URL.createObjectURL(blob));
      setQrExpiresAt(response.headers['x-share-expires-at'] || null);
    } catch (err) {
      setQrError('Failed to generate QR code.');
      console.error('Error generating report QR code:', err);
    } finally {
      setIsLoadingQr(false);
    }
  };

  const closeQrModal = () => {
    if (qrImageUrl) URL.revokeObjectURL(qrImageUrl);
    setQrModalRow(null);
    setQrImageUrl(null);
    setQrExpiresAt(null);
    setQrError(null);
    setIsLoadingQr(false);
  };

  const handleDownloadQr = () => {
    if (!qrImageUrl || !qrModalRow) return;
    const link = document.createElement('a');
    link.setAttribute('href', qrImageUrl);
    link.setAttribute('download', `report_qr_${qrModalRow.ReportID}.png`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadCSV = () => {
    if (!reportData || reportData.length === 0) {
      setError('No report data to download.');
      return;
    }

    const headers = ['Date', 'Device', 'Report ID', 'Patient ID', 'Patient Name'];
    const csvContent = [
      headers.join(','),
      ...reportData.map(row => [
        escapeCsvCell(rowDate(row)),
        escapeCsvCell(rowDevId(row)),
        escapeCsvCell(row.ReportID),
        escapeCsvCell(row.PatientID),
        escapeCsvCell(row.PatientName),
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `device_usage_report_${startDate}_to_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const COLUMNS = [
    { label: 'Date', key: 'CreatedAt' },
    ...(viewingAllDevices ? [{ label: 'Device', key: 'DevID' }] : []),
    { label: 'Patient Name', key: 'PatientName' },
    { label: 'Report ID', key: 'ReportID' },
    // { label: 'Patient ID', key: 'PatientID' },

  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800">Eye-AI Usage Report</h2>
          {devID && (
            <span className="text-sm text-gray-600">
              Device: {devID === ALL_DEVICES ? 'All devices' : devID}
            </span>
          )}
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-700 mb-4">Device Activity Report</h2>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <strong className="font-bold">Error: </strong>
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Report date range selection</label>
            <div className="flex gap-[105px] w-full max-w-md">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="w-full max-w-sm">
              <label className="block text-xs text-gray-500 mb-1">Device</label>
              <select
                value={selectedDevId}
                onChange={(e) => setSelectedDevId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {devices.length === 0 && loginDevId && (
                  <option value={loginDevId}>{loginDevId} - this device</option>
                )}
                {devices.map((device) => (
                  <option key={device.devId} value={device.devId}>{deviceLabel(device)}</option>
                ))}
                {devices.length > 1 && <option value={ALL_DEVICES}>All devices</option>}
              </select>
            </div>
          </div>

          <div className="mb-6">
            {/* <label className="block text-sm font-medium text-gray-700 mb-2">Filter results</label> */}
            <div className="w-full max-w-sm">
              <label className="block text-xs text-gray-500 mb-1">Patient Name</label>
              <input
                type="text"
                value={filterPatiName}
                onChange={(e) => setFilterPatiName(e.target.value)}
                placeholder="Search patient name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleFetchReport}
              disabled={isLoading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-300 disabled:cursor-not-allowed"
            >
              Submit
            </button>
            <button
              onClick={handleDownloadCSV}
              disabled={isLoading || reportData.length === 0}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Download size={20} />
              CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {COLUMNS.map(({ label, key }) => (
                    <th
                      key={key}
                      onClick={() => handleSort(key)}
                      className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        {sortColumn === key ? (
                          sortDirection === 'asc'
                            ? <ChevronUp className="w-4 h-4 text-indigo-600" />
                            : <ChevronDown className="w-4 h-4 text-indigo-600" />
                        ) : (
                          <ChevronsUpDown className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Download</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">View</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">QR Code</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 4} className="text-center py-10">
                      <div className="text-gray-500">Loading report data...</div>
                    </td>
                  </tr>
                ) : sortedData.length > 0 ? (
                  sortedData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700">{rowDate(row)}</td>
                      {viewingAllDevices && (
                        <td className="px-4 py-3 text-sm text-gray-700">{rowDevId(row)}</td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-700">{row.PatientName}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.ReportID}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.ReportID ? (
                          <a
                            href={`/webapi/DownloadBackupPDF?devId=${encodeURIComponent(rowDevId(row))}&reportId=${encodeURIComponent(row.ReportID)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline w-fit"
                          >
                            <Download size={16} />
                            Download
                          </a>
                        ) : ''}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.ReportID ? (
                          <button
                            type="button"
                            onClick={() => handleViewPdf(rowDevId(row), row.ReportID)}
                            className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline"
                          >
                            <Eye size={16} />
                            View
                          </button>
                        ) : ''}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.ReportID ? (
                          <button
                            type="button"
                            onClick={() => openQrModal(row)}
                            className="flex items-center gap-1 text-purple-600 hover:text-purple-800 hover:underline"
                          >
                            <QrCode size={16} />
                            QR Code
                          </button>
                        ) : ''}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.ReportID ? (
                          <button
                            type="button"
                            onClick={() => openEmailModal(row)}
                            className="flex items-center gap-1 text-teal-600 hover:text-teal-800 hover:underline"
                          >
                            <Mail size={16} />
                            Email
                          </button>
                        ) : ''}
                      </td>
                      {/* <td className="px-4 py-3 text-sm text-gray-700">{row.PatientID}</td> */}

                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={COLUMNS.length + 4} className="text-center py-10 text-gray-500">
                      No data to display.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-12 h-12 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-all flex items-center justify-center z-50"
          aria-label="Scroll to top"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}

      {qrModalRow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
            <button
              onClick={closeQrModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-medium text-gray-800 mb-1">Report QR Code</h3>
            <p className="text-sm text-gray-500 mb-4">
              {qrModalRow.PatientName} &middot; Report ID: {qrModalRow.ReportID}
            </p>

            {qrError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-3 text-sm">
                {qrError}
              </div>
            )}

            <div className="flex items-center justify-center h-64 mb-4">
              {isLoadingQr ? (
                <div className="text-gray-500 text-sm">Generating QR code...</div>
              ) : qrImageUrl ? (
                <img
                  src={qrImageUrl}
                  alt={`QR code for report ${qrModalRow.ReportID}`}
                  className="w-64 h-64 object-contain border border-gray-200 rounded"
                />
              ) : null}
            </div>

            {qrImageUrl && (
              <p className="text-xs text-gray-500 text-center mb-6">
                The patient can scan this code to download their report.
                {qrExpiresAt && <> Link expires {new Date(qrExpiresAt).toLocaleString()}.</>}
              </p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={closeQrModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={handleDownloadQr}
                disabled={!qrImageUrl}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed"
              >
                <Download size={16} />
                Download PNG
              </button>
            </div>
          </div>
        </div>
      )}

      {emailModalRow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
            <button
              onClick={closeEmailModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-medium text-gray-800 mb-1">Email Report</h3>
            <p className="text-sm text-gray-500 mb-4">Report ID: {emailModalRow.ReportID}</p>

            {emailError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-3 text-sm">
                {emailError}
              </div>
            )}
            {emailSuccess && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-3 py-2 rounded mb-3 text-sm">
                {emailSuccess}
              </div>
            )}

            <label className="block text-xs text-gray-500 mb-1">Recipient email</label>
            <input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="patient@example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-6"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={closeEmailModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={isSendingEmail || !emailInput.trim()}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-teal-300 disabled:cursor-not-allowed"
              >
                {isSendingEmail ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
